/**
 * DecisionDetailPage — /prioritization/decisions/:decisionId
 *
 * A decision's own page, the way an item has one. "Why is this the way it
 * is?" — the answer is this URL. Until 2026-10-07 this URL redirected to
 * the decision log's map with the record in its side panel; the owner
 * ruled that a thing with a URL gets a page.
 *
 * The record itself is DecisionRecordView, shared with the Decisions app.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, GitBranch, Map } from 'lucide-react';
import { useDecisionsStore } from '../../stores/prioritization/decisionsStore';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { DecisionRecordView } from '../../components/prioritization/decisions/DecisionRecordView';
import { decisionPagePath, decisionSubtitle } from '../../components/prioritization/decisions/decisionRecordText';
import type { Decision } from '../../types/prioritization/decisions';
import { FollowButton } from '../../components/follow/FollowButton';

export default function DecisionDetailPage() {
  const { decisionId } = useParams<{ decisionId: string }>();
  const { decisions, syncState, fetchDecisions } = useDecisionsStore();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (decisions.length === 0) void fetchDecisions();
  }, [decisions.length, fetchDecisions]);

  const decision: Decision | undefined = useMemo(
    () => decisions.find((d) => d.id === decisionId),
    [decisions, decisionId],
  );

  const loading = syncState.isLoading && decisions.length === 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — nothing to do */
    }
  };

  const back = (
    <Link
      to="/prioritization/decisions"
      className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
    >
      <ArrowLeft className="h-4 w-4" />
      Decisions
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {back}
        <p className="text-sm text-[var(--text-ter)]">Loading the record…</p>
      </div>
    );
  }

  if (!decision) {
    return (
      <div className="space-y-4">
        {back}
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6">
          <p className="text-base font-semibold text-[var(--text-pri)]">This decision isn't on this workspace's record</p>
          <p className="mt-1 text-sm text-[var(--text-sec)]">
            The link may belong to another workspace, or the decision may have been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {back}
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title={decision.title}
        subtitle={decisionSubtitle(decision)}
        actions={
          <div className="flex items-center gap-2">
            <FollowButton
              type="decision"
              id={decision.id}
              className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
            />
            <Link
              to={`/prioritization/decisions?view=map&selected=${decision.id}`}
              className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              title="Show this decision among the others"
            >
              <Map className="h-3.5 w-3.5" />
              See on the map
            </Link>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              title="Copy the link to this page"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-[var(--green)]" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        }
      />
      <DecisionRecordView decision={decision} decisions={decisions} decisionHref={decisionPagePath} />
    </div>
  );
}
